import * as React from "react";
import { useCallback, useEffect, useRef, useState } from "react";

import {
    InputWithTypeahead,
    TypeaheadOption,
} from "@impero-com/common/components/typeahead";
import { AjaxManager } from "@impero-com/common/utils/ajax";
import { getParentElement } from "@impero-com/common/utils/dom";
import { t } from "@impero-com/common/utils/i18n";
import { SelectProps as AntdSelectProps } from "antd/es/select";

import {
    ControlProgramOverviewResource,
    ControlProgramOverview,
} from "api/generated/model";

import { useSearchControlProgramQuery } from "./hooks/useSearchControlProgramQuery";
import * as theme from "./theme.css";

/**
 * A specialized version of the Typeahead widget, for autocompleting control program names.
 *
 * Props:
 * - `id`: the id for the basic typeahead control
 * - `placeholder`: the content of the input placeholder.
 * - `value`: the already selected controlProgramView to display in the input field. May be undefined.
 * - `ajaxManager`: the ajax manager, needed to make calls to the backend
 * - `filter`: a filter function to filter the found control programs before showing them to the user
 * - `onSelect`: callback invoked when the user picked an option. Should accept the selected option as parameter.
 */
const ControlProgramTypeahead = ({
    id,
    value,
    placeholder,
    filter,
    onSelect,
}: ControlProgramTypeaheadProps) => {
    const [search, setSearch] = useState("");
    const callbackRef = useRef<ControlProgramTypeaheadCallback>(undefined);
    const { data: matchedControlPrograms, status } =
        useSearchControlProgramQuery(search);

    useEffect(() => {
        if (status === "success" && callbackRef.current) {
            const filteredControlPrograms = filter
                ? matchedControlPrograms.filter(filter)
                : matchedControlPrograms;
            const controlProgramOptions = filteredControlPrograms.map(
                (
                    controlProgramOverviewResource: ControlProgramOverviewResource
                ) => {
                    return {
                        type: "Option" as const,
                        value: controlProgramOverviewResource.controlProgram,
                    };
                }
            );

            callbackRef.current(controlProgramOptions);
        }
    }, [matchedControlPrograms, status, filter, search]);

    const searchForControlProgram = useCallback(
        (
            search: string,
            callback: ControlProgramTypeaheadCallback
        ): Promise<void> => {
            setSearch(search);
            callbackRef.current = callback;
            return Promise.resolve(); // Useless promise, so no need to try and fake the resolution
        },
        []
    );

    return (
        <InputWithTypeahead
            id={id}
            placeholder={placeholder}
            value={value ? formatControlProgram(value) : undefined}
            formatOption={formatControlProgram}
            dropdownRender={renderDropdown}
            onSearch={searchForControlProgram}
            onSelect={onSelect}
            getPopupContainer={getParentElement}
        />
    );
};

function formatControlProgram(controlProgram: ControlProgramOverview) {
    return controlProgram.name;
}

function renderDropdown(
    menu: React.ReactElement,
    props?: AntdSelectProps,
    input?: InputWithTypeahead<ControlProgramOverview>
): React.ReactElement {
    if (input === undefined) {
        return menu;
    }
    return (
        <>
            {props && (props as any).options?.length === 0 ? (
                <div className={theme.startTyping}>
                    {t("Users_StartTyping_ControlProgram")}
                </div>
            ) : null}
            {menu}
        </>
    );
}

interface ControlProgramTypeaheadProps {
    id: string;
    value: ControlProgramOverview | undefined;
    placeholder?: string;
    ajaxManager: AjaxManager;
    filter?: (controlProgram: ControlProgramOverviewResource) => boolean;
    onSelect: (controlProgram: ControlProgramOverview) => void;
}

type ControlProgramTypeaheadCallback = (
    items: Array<TypeaheadOption<ControlProgramOverview>>
) => void;

export { ControlProgramTypeahead, type ControlProgramTypeaheadProps };
